import { Injectable } from "@angular/core";
import { ConfigService } from "./config.service";
import { ConfigResource } from "../models/config.resources.model";
import { ConfigProfile } from "../models/config.profiles.model";
import { LocalizationService } from "./localization.service";
import { DataService } from "./data.service";

export class ConfigDefaultsMismatch {
    resource: string
    fields: Array<string>
}

export class ConfigDefaultsChangedResourcePermissionRole {
    name:string

    ignore_user_group: {
        default: boolean;
        changed: boolean;
    }

    actions:Array<{ 
        action:string, 
        default:boolean, 
        changed:boolean 
    }>

    fields: Array<{ 
        name:string, 
        prop:string, 
        default:boolean, 
        changed:boolean 
    }>
}

export class ConfigDefaultsChangedResourcePermission { 
    resource: string 
    roles:Array<ConfigDefaultsChangedResourcePermissionRole> 
}

@Injectable({
    providedIn: 'root'
})
export class ConfigDefaultsService {
    constructor(private configService:ConfigService, private localizationService:LocalizationService, private dataService:DataService) {}

    checkMissingConfigurations() {
        try {
            let _missingResourceConfiguration:Array<string> = [];
            let _foundResourceConfiguration:Array<string> = [];

            for(let i in this._defaultConfigurations) {
                if(this.configService.resources.byModel[i] == null || this.configService.resources.byModel[i].id == null) {
                    _missingResourceConfiguration.push(i)
                }
                else {
                    _foundResourceConfiguration.push(i);
                }
            }

            let _missingAdminRole:Array<string> = [];

            let _missingFieldsConfiguration:Array<ConfigDefaultsMismatch> = [];
            let _missingFieldsPermissions:Array<ConfigDefaultsMismatch> = [];
            let _changedResourcePermissions:Array<ConfigDefaultsChangedResourcePermission> = [];

            let _removedFields:Array<ConfigDefaultsMismatch> = [];

            if(_foundResourceConfiguration.length > 0) {
                for(let _checkResource of _foundResourceConfiguration) {

                    if(this._noAdminResources.indexOf(_checkResource) != -1) {
                        if(this.configService.resources.byModel[_checkResource].types.user_id != null || this.configService.resources.byModel[_checkResource].types.group_id ) {
                            let _hasAdminRole = false;
    
                            for(let i in this.configService.profiles.byModel[_checkResource].roles) {
                                if(this.configService.profiles.byModel[_checkResource].roles[i].ignore_user_group) {
                                    _hasAdminRole = true;
                                    break;
                                }
                            }
    
                            if(!_hasAdminRole) {
                                _missingAdminRole.push(_checkResource)
                            }
                        }
                    }

                    let _missingConfiguration:ConfigDefaultsMismatch = null;
                    let _missingPermissions:ConfigDefaultsMismatch = null;

                    let _removedConfiguration:ConfigDefaultsMismatch = null;

                    for(let i in this.configService.resources.byModel[_checkResource].types) {
                        let _field = this.configService.resources.byModel[_checkResource].types[i];
                        
                        if(_field.missing) {
                            if(_missingConfiguration == null) {
                                _missingConfiguration = { resource: _checkResource, fields: [] };
                                _missingFieldsConfiguration.push(_missingConfiguration)
                            }
            
                            _missingConfiguration.fields.push(i)
                        }
                        else if(_field.removed) {
                            if(_removedConfiguration == null) {
                                _removedConfiguration = { resource: _checkResource, fields: [] };
                                _removedFields.push(_removedConfiguration)
                            }

                            _removedConfiguration.fields.push(i)
                        }
                    }

                    let _basePermissions = this.configService.profiles.byModel[_checkResource].roles[this.configService.profiles.baseRoleId]

                    for(let i in _basePermissions.fields) {
                        if(_basePermissions.fields[i].missing) {

                            if(_missingPermissions == null) {
                                _missingPermissions = { resource: _checkResource, fields: [] };
                                _missingFieldsPermissions.push(_missingPermissions)
                            }
            
                            _missingPermissions.fields.push(i)
                        }
                        else if(_basePermissions.fields[i].removed) {
                            if(_removedConfiguration == null) {
                                _removedConfiguration = { resource: _checkResource, fields: [] };
                                _removedFields.push(_removedConfiguration)
                            }

                            _removedConfiguration.fields.push(i)
                        }
                    }

                    let _changedPermissions:ConfigDefaultsChangedResourcePermission = null;

                    let _default = this._createDefaultConfigProfile(_checkResource)

                    for(let i in _default.roles) {
                        let _defaultRole = _default.roles[i];
                        let _checkRole = this.configService.profiles.byModel[_checkResource].roles[i]

                        let _roleName = i;

                        for(let _role of this.configService.roles) {
                            if(_role.id == i) {
                                _roleName = _role.name
                            }
                        }
    
                        let _roleDiff:ConfigDefaultsChangedResourcePermissionRole = {
                            name: _roleName,
                            ignore_user_group: null,
                            actions: [],
                            fields: []
                        }

                        let _hasSomething = false;

                        let _defaultIgnoreUserGroup = _defaultRole.ignore_user_group ? true : false;
                        let _checkIgnoreUserGroup = _checkRole.ignore_user_group ? true : false;

                        if(_defaultIgnoreUserGroup != _checkIgnoreUserGroup) {
                            _roleDiff.ignore_user_group = {
                                default: _defaultIgnoreUserGroup,
                                changed: _checkIgnoreUserGroup
                            }
                            _hasSomething = true;
                        }
    
                        for(let j in _defaultRole.actions) {
                            if(_defaultRole.actions[j] != _checkRole.actions[j]) {
                                _roleDiff.actions.push({ action: j, default: _defaultRole.actions[j], changed: _checkRole.actions[j] })
                                _hasSomething = true;
                            }
                        }
    
                        for(let j in _defaultRole.fields) {
                            for(let k of ['view','edit']) {
                                let _defaultValue = _defaultRole.fields[j][k]
                                let _checkValue =  _checkRole.fields[j][k]

                                if(_defaultValue != _checkValue) {
                                    _roleDiff.fields.push({ 
                                        name: j, 
                                        prop: k, 
                                        default: _defaultValue, 
                                        changed: _checkValue 
                                    })
                                    
                                    _hasSomething = true;
                                }
                            }
                        }
    
                        if(_hasSomething) {
                            if(_changedPermissions == null) {
                                _changedPermissions = {
                                    resource: _checkResource,
                                    roles: []
                                }
                            }
    
                            _changedPermissions.roles.push(_roleDiff)
                        }
                    }
    
                    if(_changedPermissions != null) {
                        _changedResourcePermissions.push(_changedPermissions)
                    }
                }
            }

            return {
                foundResourceConfiguration: _foundResourceConfiguration,
                missingAdminRole: _missingAdminRole,
                missingResourceConfiguration: _missingResourceConfiguration,
                missingFieldsConfiguration: _missingFieldsConfiguration,
                missingFieldsPermissions: _missingFieldsPermissions,
                removedFields: _removedFields,
                changedResourcePermissions: _changedResourcePermissions,
            }
        }
        catch(ex) {
            console.error(ex)
        }
    }

    private _noAdminResources = ['notification']

    private _defaultConfigurationEFResource(config:ConfigResource) {
        config.general.category = 'DMS'

        let _nameOrTitle = 'name'
        if(config.types['name'] == null) {
            _nameOrTitle = 'title'
        }

        config.general.roles.select = '{{id}} - {{' + _nameOrTitle + '}}'
        config.general.roles.title = '{{id}} - {{' + _nameOrTitle + '}}'
        config.general.roles.abstract = '';
        config.general.roles.description = '{{description}}'

        if(config.types.coordinates != null) config.types.coordinates.type = 'location'
        if(config.types.description != null) config.types.description.type = 'html'

        if(config.types.videos != null) {
            config.types.videos.type = 'file'
            config.types.videos.fileType = 'videos'
            config.types.videos.multi = true;
        }

        if(config.types.cover != null) {
            config.types.cover.type = 'file'
            config.types.cover.fileType = 'images'
            config.general.roles.image = '{{cover}}'
        }

        for(let i in config.types) {
            if(/^images/.test(i)) {
                config.types[i].type = 'file'
                config.types[i].fileType = 'images'
                config.types[i].multi = true;
            }
            else {
                switch(i) {
                    case 'languages':
                    case 'system_tags': 
                        config.types[i].multi = true; break;
                }
            }
        }

        let _columns = [
            {
                field: "id"
            },
            {
                field: _nameOrTitle
            },
            {
                field: "type"
            },
            {
                field: "category"
            },
            {
                field: "stars"
            },
            {
                field: "city"
            },
            {
                field: "tags"
            },
        ]

        for(let i = _columns.length - 1; i >= 0; i--) {
            if(_columns[i].field != null) {
                if(config.types[_columns[i].field] == null) {
                    _columns.splice(i, 1);
                }
            }
        }
        
        config.view = {
            name: "Table",
            type: "Table",
            resource: config.model,
            config: {
                "columns": _columns
            }
        }

        return config;
    }

    private _defaultConfigurationEFProfile(config:ConfigProfile, minRole?:'business'|'editor') {
        let _systemFields = ['id','created_at','updated_at','deleted_at','imported']
        let _editorFelds = ['published','visit_card_supplier','event_organizer']
        let _adminFields = ['user_id','group_id']

        let _grantRoles:Array<string> = [];
        if(minRole == 'business') _grantRoles = ['business', 'editor', 'admin'];
        else if(minRole == 'editor') _grantRoles = ['editor', 'admin'];

        for(let _role of this.configService.roles) {
            if(_role.name == 'base') {
                for(let i in config.roles[_role.id].actions) {
                    config.roles[_role.id].actions[i] = minRole != null ? false : true;
                }

                for(let i in config.roles[_role.id].fields) {
                    config.roles[_role.id].fields[i].view = true;

                    if(_systemFields.indexOf(i) == -1) {
                        if(_editorFelds.indexOf(i) == -1 && _adminFields.indexOf(i) == -1) {
                            config.roles[_role.id].fields[i].edit = true;
                        }
                        else {
                            config.roles[_role.id].fields[i].edit = false;
                        }
                    }
                }
            }
            else if(_grantRoles.indexOf(_role.name) != -1) {
                for(let i in config.roles[_role.id].actions) {
                    config.roles[_role.id].actions[i] = true;
                }

                if(_role.name == 'admin' || _role.name == 'editor') {
                    config.roles[_role.id].ignore_user_group = true;
                    
                    for(let i in config.roles[_role.id].fields) {
                        if(_editorFelds.indexOf(i) != -1 || (_role.name == 'admin' && _adminFields.indexOf(i) != -1)) {
                            config.roles[_role.id].fields[i].edit = true;
                        }
                    }
                }
            }
        }

        return config;
    }

    private _defaultConfigurations: { 
        [resource:string]: {
            resource:(config:ConfigResource) => ConfigResource,
            profile:(config:ConfigProfile) => ConfigProfile,
        }
    } = {
        'User': {
            resource: (config:ConfigResource) => {
                config.general.roles.title = '{{id}} - {{name}} {{surname}}'
                config.general.roles.select = '{{id}} - {{name}} {{surname}}'
                config.general.roles.abstract = '{{email}} {{telephone}}'
                config.general.roles.description = ''

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "User",
                    config: {
                        "columns": [{
                            "field": "id",
                            "sum": null
                        }, {
                            "name": "Name Surname",
                            "roleview": "{{name}} {{surname}}"
                        }, {
                            "field": "email",
                            "sum": null
                        }, {
                            "field": "telephone",
                            "sum": null
                        }, {
                            "field": "system",
                            "sum": null
                        }, {
                            "field": "superadmin",
                            "sum": null
                        }]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }

                        for(let i in config.roles[_role.id].fields) {
                            config.roles[_role.id].fields[i].view = true;

                            if(i == 'system' || i == 'superadmin') {
                                config.roles[_role.id].fields[i].edit = false; 
                            }
                        }
                    }
                    else if(_role.name == 'admin' || _role.name == 'socket') {
                        config.roles[_role.id].ignore_user_group = true;

                        config.roles[_role.id].actions.list = true;
                        config.roles[_role.id].actions.view = true;
                    }
                }

                return config;
            }
        },
        'Role': {
            resource: (config:ConfigResource) => {
                return config
            },
            profile: (config:ConfigProfile) => {
                let _baseActions = ['list', 'view']

                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = _baseActions.indexOf(i) != -1;
                        }
                    }
                    /*else if(_role.name == 'admin') {
                        for(let i in config.roles[_role.id].actions) {
                            if(_baseActions.indexOf(i) == -1) {
                                config.roles[_role.id].actions[i] = true;
                            }
                        }
                    }*/
                }

                return config;
            }
        },
        'RoleUser': {
            resource: (config:ConfigResource) => {
                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }
                    }
                    else if(_role.name == 'admin') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }

                        config.roles[_role.id].ignore_user_group = true;
                    }
                }

                return config;
            }
        },
        'Profile': {
            resource: (config:ConfigResource) => {
                config.general.roles.title = '{{id}} - {{user_id}}'
                config.general.roles.select = '{{id}} - {{user_id}}'
                config.general.roles.abstract = ''
                config.general.roles.description = ''

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "Profile",
                    config: {
                        "columns": [{
                            "field": "id",
                            "sum": null
                        }, {
                            "field": "user_id",
                            "sum": null
                        }]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {

                    let _readonly = ['id','created_at','updated_at','deleted_at']
                    let _baseHidden = ['customer_care','customer_care_languages']

                    if(_role.name == 'base') {
                        config.roles[_role.id].actions.list = false
                        config.roles[_role.id].actions.create = false

                        config.roles[_role.id].actions.view = true
                        config.roles[_role.id].actions.edit = true
                        config.roles[_role.id].actions.delete = true

                        for(let i in config.roles[_role.id].fields) {
                            if(_baseHidden.indexOf(i) == -1) {
                                config.roles[_role.id].fields[i].view = true;

                                if(_readonly.indexOf(i) == -1) {
                                    config.roles[_role.id].fields[i].edit = true;
                                }
                                else {
                                    config.roles[_role.id].fields[i].edit = false; 
                                }
                            }
                            else {
                                config.roles[_role.id].fields[i].view = false;
                                config.roles[_role.id].fields[i].edit = false;
                            }
                        }
                    }
                    else if(_role.name == 'admin') {
                        config.roles[_role.id].ignore_user_group = true;

                        config.roles[_role.id].actions.list = true
                        config.roles[_role.id].actions.create = true

                        for(let i in config.roles[_role.id].fields) {
                            if(_baseHidden.indexOf(i) != -1) {
                                config.roles[_role.id].fields[i].view = true;
                                config.roles[_role.id].fields[i].edit = true;
                            }
                        }
                    }
                    else if(_role.name == 'socket') {
                        config.roles[_role.id].ignore_user_group = true;

                        config.roles[_role.id].actions.list = true
                    }
                }

                return config;
            }
        },
        'Tag': {
            resource: (config:ConfigResource) => {
                config.general.roles.title = '{{id}} {{label}}'
                config.general.roles.abstract = '{{id}} {{label}}'
                config.general.roles.description = ''

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "Tag",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "label"
                            }
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = (i == 'list' || i == 'view');
                        }

                        for(let i in config.roles[_role.id].fields) {
                            config.roles[_role.id].fields[i].view = true;
                            config.roles[_role.id].fields[i].edit = false;
                        }
                    }
                }

                return config;
            }
        },
        'SystemTag': {
            resource: (config:ConfigResource) => {
                config.general.roles.title = '{{id}} {{label}}'
                config.general.roles.abstract = '{{id}} {{label}}'
                config.general.roles.description = ''

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "SystemTag",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "label"
                            }
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {

                let _baseActions = ['list', 'view']

                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = _baseActions.indexOf(i) != -1;
                        }

                        for(let i in config.roles[_role.id].fields) {
                            config.roles[_role.id].fields[i].view = true;
                            config.roles[_role.id].fields[i].edit = false;
                        }
                    }
                    else if(_role.name == 'admin') {
                        for(let i in config.roles[_role.id].actions) {
                            if(_baseActions.indexOf(i) == -1) {
                                config.roles[_role.id].actions[i] = true;
                            }
                        }

                        for(let i in config.roles[_role.id].fields) {
                            if(i != 'id' && i != 'created_at' && i != 'updated_at' && i != 'deleted_at') {
                                config.roles[_role.id].fields[i].view = null;
                                config.roles[_role.id].fields[i].edit = true;
                            }
                        }
                    }
                }

                return config;
            }
        },
        'Group': {
            resource: (config:ConfigResource) => {
                config.general.roles.title = '{{id}} {{label}}'
                config.general.roles.abstract = '{{id}} {{label}}'
                config.general.roles.description = ''

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "Group",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "label"
                            }
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }

                        for(let i in config.roles[_role.id].fields) {
                            config.roles[_role.id].fields[i].view = true;
                            config.roles[_role.id].fields[i].edit = false;
                        }
                    }
                    else if(_role.name == 'admin') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }

                        for(let i in config.roles[_role.id].fields) {
                            if(i != 'id' && i != 'created_at' && i != 'updated_at' && i != 'deleted_at') {
                                config.roles[_role.id].fields[i].view = null;
                                config.roles[_role.id].fields[i].edit = true;
                            }
                        }
                    }
                }

                return config;
            }
        },
        'UserGroup': {
            resource: (config:ConfigResource) => {
                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }

                        for(let i in config.roles[_role.id].fields) {
                            config.roles[_role.id].fields[i].view = true;
                            
                            if(i != 'id' && i != 'created_at' && i != 'updated_at') {
                                config.roles[_role.id].fields[i].edit = true;
                            }
                        }
                    }
                    else if(_role.name == 'admin') {
                        config.roles[_role.id].ignore_user_group = true;

                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }
                    }
                }

                return config;
            }
        },
        'UserRealm': {
            resource: (config:ConfigResource) => {
                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }

                        for(let i in config.roles[_role.id].fields) {
                            config.roles[_role.id].fields[i].view = true;
                            
                            if(i != 'id' && i != 'created_at' && i != 'updated_at') {
                                config.roles[_role.id].fields[i].edit = true;
                            }
                        }
                    }
                    else if(_role.name == 'admin') {
                        config.roles[_role.id].ignore_user_group = true;

                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }
                    }
                }

                return config;
            }
        },
        'Content': {
            resource: (config:ConfigResource) => {

                let _columnsConfig = {
                    "columns": [
                        {
                            field: "id"
                        },
                        {
                            field: "tid"
                        }
                    ]
                }

                for(let _language of this.localizationService.availableApplicationLanguages) {
                    for(let _field of this.configService.models[config.model]) {
                        if(_field.name == _language) {
                            _columnsConfig.columns.push({
                                field: _field.name
                            })

                            config.types[_field.name].type = 'text';
                        }
                    }
                }

                config.general.roles.title = '{{tid}}'
                config.general.roles.abstract = '{{' + _columnsConfig.columns[2].field + '}} {{' + _columnsConfig.columns[3].field + '}}'
                config.general.roles.description = ''

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "Content",
                    config: _columnsConfig
                }

                config.types.tid.required = true;

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }

                        for(let i in config.roles[_role.id].fields) {
                            config.roles[_role.id].fields[i].view = true;
                            config.roles[_role.id].fields[i].edit = false;
                        }
                    }
                    else if(_role.name == 'admin') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }

                        for(let i in config.roles[_role.id].fields) {
                            if(i != 'id' && i != 'created_at' && i != 'updated_at' && i != 'deleted_at') {
                                config.roles[_role.id].fields[i].view = null;
                                config.roles[_role.id].fields[i].edit = true;
                            }
                        }
                    }
                }

                return config;
            }
        },
        'File': {
            resource: (config:ConfigResource) => {
                config.general.roles.abstract = '{{nome_file}} {{category}}'
                config.general.roles.description = '{{url}}'

                config.view = {
                    name: "Files",
                    type: "Files",
                    resource: "File",
                    config: null
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }

                        for(let i in config.roles[_role.id].fields) {
                            config.roles[_role.id].fields[i].view = true;

                            if(i != 'id' && i != 'created_at' && i != 'updated_at' && i != 'deleted_at') {
                                config.roles[_role.id].fields[i].edit = true;
                            }
                            else {
                                config.roles[_role.id].fields[i].edit = false;
                            }
                        }
                    }
                    else if(_role.name == 'admin' || _role.name == 'editor') {
                        config.roles[_role.id].ignore_user_group = true;
                    }
                }

                return config;
            }
        }, 
        'Directory': {
            resource: (config:ConfigResource) => {
                config.general.roles.select = '{{id}} - {{label}}'
                config.general.roles.title = '{{id}} - {{label}}'
                config.general.roles.abstract = '{{path}}'
                config.general.roles.description = '{{path}}'

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        config.roles[_role.id].ignore_user_group = true;

                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }

                        for(let i in config.roles[_role.id].fields) {
                            config.roles[_role.id].fields[i].view = true;

                            if(i != 'id' && i != 'created_at' && i != 'updated_at' && i != 'deleted_at') {
                                config.roles[_role.id].fields[i].edit = true;
                            }
                            else {
                                config.roles[_role.id].fields[i].edit = false;
                            }
                        }
                    }
                    else if(_role.name == 'admin' || _role.name == 'editor') {
                        config.roles[_role.id].ignore_user_group = true;
                    }
                }

                return config;
            }
        }, 
        'Experience': {
            resource: (config:ConfigResource) => {
                config.general.category = 'DMS'

                config.general.roles.slug = '{{title}}'
                config.general.roles.select = '{{id}} - {{title}}'
                config.general.roles.title = '{{title}}'
                config.general.roles.description = '{{description}}'
                
                config.types.coordinates.type = 'location'
                config.types.description.type = 'html'
                config.types.cost_range.type = 'rating'
                config.types.minimum_guaranteed.type = 'slider'
                config.types.minimum_guaranteed.min = 1
                config.types.minimum_guaranteed.max = 20
                config.types.broker_discount.min = 0
                config.types.broker_discount.max = 100
                config.types.cancellation_deadline.type = 'slider'
                config.types.cancellation_deadline.min = 1
                config.types.cancellation_deadline.max = 60
                config.types.tickets.type = 'tickets-experience'
                config.types.payment.multi = true;
                config.types.availability.type = 'timetable-dates';
                
                config.types.videos.type = 'file'
                config.types.videos.fileType = 'videos'
                config.types.videos.multi = true;
                config.types.images.type = 'file'
                config.types.images.fileType = 'images'
                config.types.images.multi = true;

                //config.types.certificate.type = 'boolean'
                config.types.published.type = 'boolean'
                config.types.by_organization.type = 'boolean'

                config.types.languages.multi = true
                config.types.system_tags.multi = true

                config.types.poi_id.resourceMode = 'public'

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "Experience",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "title"
                            },
                            {
                                name: "RESOURCES.Experience.fields.category.label",
                                cluster: "category,subcategory"
                            },
                            {
                                field: "tags"
                            },
                            {
                                name: "RESOURCES.Experience.fields.city.label",
                                roleview: "{{#related_poi_id}}{{related_poi_id.city}}{{/related_poi_id}}{{#city}}{{city}}{{/city}}"
                            },
                            {
                                field: "availability"
                            }
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                let _systemFields = ['id','created_at','updated_at','deleted_at','imported']
                let _adminFields = ['published','by_organization','user_id','group_id']

                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }

                        for(let i in config.roles[_role.id].fields) {
                            config.roles[_role.id].fields[i].view = true;

                            if(_systemFields.indexOf(i) == -1) {
                                if(_adminFields.indexOf(i) == -1) {
                                    config.roles[_role.id].fields[i].edit = true;
                                }
                                else {
                                    config.roles[_role.id].fields[i].edit = false;
                                }
                            }
                            else {
                                config.roles[_role.id].fields[i].edit = false;
                            }
                        }
                    }
                    else if(_role.name == 'business') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }
                    }
                    else if(_role.name == 'admin' || _role.name == 'editor') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }

                        config.roles[_role.id].ignore_user_group = true;

                        for(let i in config.roles[_role.id].fields) {
                            if(_adminFields.indexOf(i) != -1) {
                                config.roles[_role.id].fields[i].edit = true;
                            }
                        }
                    }
                }

                return config;
            }
        }, 
        'Host': {
            resource: (config:ConfigResource) => {
                config = this._defaultConfigurationEFResource(config)

                config.types.stars.type = 'rating'
                
                return config;
            },
            profile: (config:ConfigProfile) => {
                config = this._defaultConfigurationEFProfile(config, 'business')
                return config
            }
        }, 
        'Eatery': {
            resource: (config:ConfigResource) => {
                config = this._defaultConfigurationEFResource(config)
                return config;
            },
            profile: (config:ConfigProfile) => {
                config = this._defaultConfigurationEFProfile(config, 'business')
                return config
            }
        }, 
        'ExperienceSupplier': {
            resource: (config:ConfigResource) => {
                config = this._defaultConfigurationEFResource(config)
                return config;
            },
            profile: (config:ConfigProfile) => {
                config = this._defaultConfigurationEFProfile(config, 'business')
                return config
            }
        },
        'Good': {
            resource: (config:ConfigResource) => {
                config = this._defaultConfigurationEFResource(config)

                if(config.types.published != null) {
                    config.types.published.type = 'boolean'
                }

                return config;
            },
            profile: (config:ConfigProfile) => {
                config = this._defaultConfigurationEFProfile(config, 'business')
                return config
            }
        },
        'Poi': {
            resource: (config:ConfigResource) => {
                config = this._defaultConfigurationEFResource(config)
                return config;
            },
            profile: (config:ConfigProfile) => {
                config = this._defaultConfigurationEFProfile(config, 'editor')
                return config
            }
        }, 
        'Itinerary': {
            resource: (config:ConfigResource) => {
                config = this._defaultConfigurationEFResource(config)
                return config;
            },
            profile: (config:ConfigProfile) => {
                config = this._defaultConfigurationEFProfile(config, 'editor')
                return config
            }
        }, 
        'Article': {
            resource: (config:ConfigResource) => {
                config = this._defaultConfigurationEFResource(config)

                config.types.tags.type = 'string'
                config.types.category.type = 'select'
                config.types.coordinates.type = 'location'
                config.types.text.type = 'html'
                config.types.language.type = 'select'
                config.types.priority.type = 'select'

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "Article",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "title"
                            },
                            {
                                field: "category"
                            },
                            {
                                name: "RESOURCES.Article.fields.city.label",
                                roleview: "{{#related_poi_id}}{{related_poi_id.city}}{{/related_poi_id}}{{#city}}{{city}}{{/city}}"
                            },
                            {
                                field: "tags"
                            }
                        ]
                    }
                }

                return config;
            },
            profile: (config:ConfigProfile) => {
                config = this._defaultConfigurationEFProfile(config, 'editor')
                return config
            }
        },  
        'TourProduct': {
            resource: (config:ConfigResource) => {
                config.general.roles.select = '{{id}} - {{name}}'
                config.general.roles.title = '{{name}}'
                config.general.roles.abstract = '{{description}}'
                config.general.roles.description = '';
                config.general.roles.image = '{{{content.image}}}'
                config.general.roles.location = '{{content.location}}'
                config.general.roles.geoloc = '{{coordinates}}'
                config.general.roles.price = '{{price}}'

                config.view = {
                    name: "Cards",
                    type: "Cards",
                    resource: "TourProduct",
                    config: null
                }

                config.types['content'].type = 'json'

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }

                        for(let i in config.roles[_role.id].fields) {
                            if(i == 'content') {
                                config.roles[_role.id].fields[i].view = false;
                                config.roles[_role.id].fields[i].edit = false;
                            }
                            else {
                                config.roles[_role.id].fields[i].view = true;

                                if(i != 'id' && i != 'created_at' && i != 'updated_at' && i != 'deleted_at') {
                                    config.roles[_role.id].fields[i].edit = true;
                                }
                                else {
                                    config.roles[_role.id].fields[i].edit = false;
                                }
                            }
                        }
                    }
                    else if(_role.name == 'admin' || _role.name == 'editor' || _role.name == 'socket') {
                        config.roles[_role.id].ignore_user_group = true;
                        config.roles[_role.id].fields['content'].edit = true;
                        config.roles[_role.id].fields['content'].view = true;
                    }
                }

                return config;
            }
        }, 
        'Booking': {
            resource: (config:ConfigResource) => {
                config.general.category = 'DMS'

                config.general.roles.select = '{{id}} - {{pid}}'
                config.general.roles.title = '{{id}} - {{pid}}'
                config.general.roles.abstract = ''
                config.general.roles.description = '';
                config.general.roles.image = ''
                config.general.roles.location = ''
                config.general.roles.geoloc = ''
                config.general.roles.price = ''

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "Booking",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "pid"
                            },
                            {
                                field: "experience_id"
                            },
                            {
                                field: "date"
                            },
                            {
                                field: "user_email"
                            },
                            {
                                field: "user_phone"
                            }
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        config.roles[_role.id].actions.list = true;
                        config.roles[_role.id].actions.view = true;
                        config.roles[_role.id].actions.edit = false;
                        config.roles[_role.id].actions.create = true;
                        config.roles[_role.id].actions.delete = false;

                        for(let i in config.roles[_role.id].fields) {
                            config.roles[_role.id].fields[i].view = true;

                            if(i != 'id' && i != 'created_at' && i != 'updated_at' && i != 'deleted_at' && i != 'status') {
                                config.roles[_role.id].fields[i].edit = i != 'user_id';
                            }
                            else {
                                config.roles[_role.id].fields[i].edit = false
                            }
                        }
                    }
                    else if(_role.name == 'admin') {
                        config.roles[_role.id].actions.edit = true;
                        config.roles[_role.id].actions.delete = true;

                        config.roles[_role.id].ignore_user_group = true;

                        for(let i in config.roles[_role.id].fields) {
                            if(i == 'user_id') {
                                config.roles[_role.id].fields[i].edit = true;
                            }
                        }
                    }
                }

                return config;
            }
        }, 
        'AccessToken': {
            resource: (config:ConfigResource) => {
                config.general.category = 'DMS'

                config.general.roles.select = '{{id}} - {{resource}} {{resource_id}}'
                config.general.roles.title = '{{id}} - {{resource}} {{resource_id}}'
                config.general.roles.abstract = '{{activation_time}}'
                config.general.roles.description = '';
                config.general.roles.image = ''
                config.general.roles.location = ''
                config.general.roles.geoloc = ''
                config.general.roles.price = ''

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "AccessToken",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "resource"
                            },
                            {
                                field: "resource_id"
                            },
                            {
                                field: "activation_time"
                            }
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }

                        for(let i in config.roles[_role.id].fields) {
                            config.roles[_role.id].fields[i].view = true;
                            config.roles[_role.id].fields[i].edit = false
                        }
                    }
                    else if(_role.name == 'admin') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }

                        for(let i in config.roles[_role.id].fields) {
                            if(i != 'id' && i != 'created_at' && i != 'updated_at' && i != 'deleted_at') {
                                config.roles[_role.id].fields[i].edit = true;
                            }
                        }
                    }
                    else if(_role.name == 'socket') {
                        config.roles[_role.id].actions.list = true;
                        config.roles[_role.id].actions.view = true;
                        config.roles[_role.id].actions.edit = true;
                        config.roles[_role.id].fields['activation_time'].edit = true;
                    }
                }

                return config;
            }
        },
        'ChatSession': {
            resource: (config:ConfigResource) => {
                config.general.category = 'Chat'

                config.general.roles.select = '{{id}} - {{username}}'
                config.general.roles.title = '{{id}} - {{username}}'
                config.general.roles.abstract = ''
                config.general.roles.description = '';
                config.general.roles.image = ''
                config.general.roles.location = ''
                config.general.roles.geoloc = ''
                config.general.roles.price = ''

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "ChatSession",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "username"
                            },
                            {
                                field: "user_id"
                            },
                            {
                                field: "session_id"
                            },
                            {
                                field: "created_at"
                            },
                            {
                                field: "closed_at"
                            },
                            {
                                field: "message_count"
                            },
                            {
                                field: "pid"
                            },
                            {
                                field: "data"
                            }
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }
                    }
                    else if(_role.name == 'socket') {
                        config.roles[_role.id].actions.list = true;
                        config.roles[_role.id].actions.view = true;
                        config.roles[_role.id].actions.edit = true;
                        config.roles[_role.id].actions.create = true;
                    }
                    else if(_role.name == 'admin') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }
                    }
                }

                return config;
            }
        }, 
        'ChatMessage': {
            resource: (config:ConfigResource) => {
                config.general.category = 'Chat'

                config.general.roles.select = '{{id}} - {{from}} {{to}}'
                config.general.roles.title = '{{id}} - {{from}} {{to}}'
                config.general.roles.abstract = ''
                config.general.roles.description = '';
                config.general.roles.image = ''
                config.general.roles.location = ''
                config.general.roles.geoloc = ''
                config.general.roles.price = ''

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "ChatMessage",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "from"
                            },
                            {
                                field: "to"
                            },
                            {
                                field: "content"
                            },
                            {
                                field: "data"
                            },
                            {
                                field: "created_at"
                            },
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }
                    }
                    else if(_role.name == 'socket') {
                        config.roles[_role.id].actions.list = true;
                        config.roles[_role.id].actions.view = true;
                        config.roles[_role.id].actions.edit = true;
                        config.roles[_role.id].actions.create = true;
                    }
                    else if(_role.name == 'admin') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }
                    }
                }

                return config;
            }
        },
        'CallLog': {
            resource: (config:ConfigResource) => {
                config.general.category = 'Chat'
                
                config.general.roles.select = '{{id}} - {{channel}} {{username}}'
                config.general.roles.title = '{{id}} - {{channel}} {{username}}'
                config.general.roles.abstract = ''
                config.general.roles.description = '';
                config.general.roles.image = ''
                config.general.roles.location = ''
                config.general.roles.geoloc = ''
                config.general.roles.price = ''

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "CallLog",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "channel"
                            },
                            {
                                field: "username"
                            },
                            {
                                field: "call_id"
                            },
                            {
                                field: "in"
                            },
                            {
                                field: "out"
                            },
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }
                    }
                    else if(_role.name == 'socket') {
                        config.roles[_role.id].actions.list = true;
                        config.roles[_role.id].actions.view = true;
                        config.roles[_role.id].actions.edit = true;
                        config.roles[_role.id].actions.create = true;
                    }
                    else if(_role.name == 'admin') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }
                    }
                }

                return config;
            }
        }, 
        'Notification': {
            resource: (config:ConfigResource) => {
                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }

                        config.roles[_role.id].actions.list = true;
                        config.roles[_role.id].actions.view = true;
                        config.roles[_role.id].actions.edit = true;

                        for(let i in config.roles[_role.id].fields) {
                            config.roles[_role.id].fields[i].view = true;
                            config.roles[_role.id].fields[i].edit = i == 'status';
                        }
                    }
                }

                return config;
            }
        },
        'TotemEnvironment': {
            resource: (config:ConfigResource) => {
                config.general.category = 'Totem'

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "TotemEnvironment",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "pid"
                            },
                            {
                                field: "configuration"
                            },
                            {
                                field: "data"
                            }
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }
                    }
                    else if(_role.name == 'admin') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }
                    }
                }

                return config;
            }
        },
        'FinderEnvironment': {
            resource: (config:ConfigResource) => {
                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "FinderEnvironment",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "pid"
                            },
                            {
                                field: "data"
                            }
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }
                    }
                    else if(_role.name == 'admin') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }
                    }
                }

                return config;
            }
        },
        'Faq': {
            resource: (config:ConfigResource) => {
                config.general.category = 'DMS'

                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "Faq",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "title"
                            },
                            {
                                field: "destination"
                            },
                            {
                                field: "content"
                            }
                        ]
                    }
                }

                config.types.title.required = true;
                config.types.content.required = true;

                let _fieldsHidden = ['user_id']
                let _fieldsFullWidth = ['content']

                let _fieldList = config.form.layout.groups[0].fields

                for(let i = _fieldList.length - 1; i >= 0; i--) {
                    let _field = _fieldList[i];

                    if(_fieldsHidden.indexOf(_field.name) != -1) {
                        _field.hidden = true;
                    }
                    else {
                        if(_fieldsFullWidth.indexOf(_field.name) != -1) {
                            _field.display = { fullWidth: true };
                        }
                    }
                }
                
                let _fieldsPushBack = ['group_id','realm_id']

                for(let j = _fieldsPushBack.length - 1; j >= 0; j--) {
                    for(let i = 0; i < _fieldList.length; i++) {
                        let _field = _fieldList[i];

                        if(_field.name == _fieldsPushBack[j]) {
                            _fieldList.splice(i, 1)
                            _fieldList.push(_field)
                            break;
                        }
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }
                    }
                    else if(_role.name == 'admin' || _role.name == 'editor') {
                        config.roles[_role.id].ignore_user_group = true;

                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }
                    }
                }

                return config;
            }
        },
        'VisitCard': {
            resource: (config:ConfigResource) => {
                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "VisitCard",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "code"
                            },
                            {
                                field: "activated_at"
                            },
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                let _baseActions = ['view','list']

                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = _baseActions.indexOf(i) != -1;
                        }
                    }
                    else if(_role.name == 'admin') {
                        config.roles[_role.id].ignore_user_group = true;

                        for(let i in config.roles[_role.id].actions) {
                            if(_baseActions.indexOf(i) == -1) {
                                config.roles[_role.id].actions[i] = true;
                            }
                        }
                    }
                }

                return config;
            }
        },
        'CheckinPoint': {
            resource: (config:ConfigResource) => {
                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "CheckinPoint",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "code"
                            },
                            {
                                field: "name"
                            },
                            {
                                field: "description"
                            }
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = false;
                        }
                    }
                    else if(_role.name == 'admin') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = true;
                        }
                    }
                }

                return config;
            }
        },
        'Checkin': {
            resource: (config:ConfigResource) => {
                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "Checkin",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "checkin_point_id"
                            },
                            {
                                field: "visit_card_code"
                            },
                            {
                                field: "datetime"
                            },
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                let _baseActions = ['view','list','create']

                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = _baseActions.indexOf(i) != -1;
                        }
                    }
                    else if(_role.name == 'admin') {
                        config.roles[_role.id].ignore_user_group = true;

                        for(let i in config.roles[_role.id].actions) {
                            if(_baseActions.indexOf(i) == -1) {
                                config.roles[_role.id].actions[i] = true;
                            }
                        }
                    }
                }

                return config;
            }
        },
        'VisitCardService': {
            resource: (config:ConfigResource) => {
                config.view = {
                    name: "Table",
                    type: "Table",
                    resource: "VisitCardService",
                    config: {
                        "columns": [
                            {
                                field: "id"
                            },
                            {
                                field: "name"
                            },
                            {
                                field: "description"
                            }
                        ]
                    }
                }

                return config
            },
            profile: (config:ConfigProfile) => {
                let _baseActions = []

                for(let _role of this.configService.roles) {
                    if(_role.name == 'base') {
                        for(let i in config.roles[_role.id].actions) {
                            config.roles[_role.id].actions[i] = _baseActions.indexOf(i) != -1;
                        }
                    }
                    else if(_role.name == 'admin') {
                        config.roles[_role.id].ignore_user_group = true;

                        for(let i in config.roles[_role.id].actions) {
                            if(_baseActions.indexOf(i) == -1) {
                                config.roles[_role.id].actions[i] = true;
                            }
                        }
                    }
                }

                return config;
            }
        },
    }

    private _createDefaultConfigResource(resource:string) {
        return this._defaultConfigurations[resource].resource(new ConfigResource(resource, null, this.configService.models[resource], this.configService.relations.byModel[resource]));
    }

    private _createDefaultConfigProfile(resource:string) {
        // TOFIX: questa doppia creazione dell'oggetto serve perché la default configuration si applica su una configurazione esistente, ma vanno riapplicate le modifiche dal model (ie: campi readonly da modello non modificabili)

        let _retVal = new ConfigProfile(resource, null, this.configService.roles, this.configService.models[resource], this.configService.relations.byModel[resource]);
        _retVal = this._defaultConfigurations[resource].profile(_retVal);

        return new ConfigProfile(resource, _retVal.toData(false), this.configService.roles, this.configService.models[resource], this.configService.relations.byModel[resource])
    }

    createDefaultConfiguration(resource:string) {
        return new Promise<void>((resolve, reject) => {
            if(this._defaultConfigurations[resource] == null) {
                reject()
            }
            else {
                let _configResource = this._createDefaultConfigResource(resource)
                let _configProfile = this._createDefaultConfigProfile(resource)

                //this.dataService.clearCache(); 

                let _saveId = this.configService.resources.byModel[resource]?.id
                let _saveData = _configResource.toData(true)
                _saveData.id = _saveId;

                this.dataService.saveConfigData(_saveId, _saveData).subscribe(() => {
                    let _reqCount = 0;

                    let _savedProfile = this.configService.profiles.byModel[resource]

                    if(_savedProfile != null) {
                        for(let i in _savedProfile.roles) {
                            if(_configProfile.roles[i] == null) {
                                _reqCount++;

                                let _saveId = _savedProfile?.roles[i]?.id

                                this.dataService.deletePermissionData(_saveId).subscribe(() => {
                                    _reqCount--;
                                    if(_reqCount == 0) {
                                        resolve();
                                    }
                                }) // gestione errori?
                            }
                        }
                    }

                    for(let i in _configProfile.roles) {
                        _reqCount++;

                        let _saveId = _savedProfile?.roles[i]?.id
                        let _saveData = _configProfile.roles[i].toData(true)
                        _saveData.id = _saveId;

                        this.dataService.savePermissionData(_saveId, _saveData).subscribe(() => {
                            _reqCount--;
                            if(_reqCount == 0) {
                                resolve();
                            }
                        }) // gestione errori?
                    }

                    if(_reqCount == 0) {
                        resolve()
                    }
                })
            }
        })
    }
}